import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { clientsApi, pitchesApi, pitchingApi, googleApi } from '../utils/api';
import { useToast } from '../hooks/useToast';

const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

const NAMED_TIMEZONES = [
  { value: 'America/Los_Angeles', label: 'Pacific Time (PST/PDT)' },
  { value: 'America/Denver', label: 'Mountain Time (MST/MDT)' },
  { value: 'America/New_York', label: 'Eastern Time (EST/EDT)' },
];
const TIMEZONE_OPTIONS = [
  { value: LOCAL_TZ, label: `Auto-detected — ${LOCAL_TZ.replace(/_/g, ' ')}` },
  ...NAMED_TIMEZONES.filter(z => z.value !== LOCAL_TZ),
];

// 15-minute increments, 12-hour display (e.g. "2:30 PM") — matches Google
// Calendar's picker. Shown as datalist suggestions on a plain text input so
// typing an exact time still works.
const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const period = h < 12 ? 'AM' : 'PM';
      const hour12 = h % 12 === 0 ? 12 : h % 12;
      slots.push(`${hour12}:${String(m).padStart(2, '0')} ${period}`);
    }
  }
  return slots;
})();

// Lenient parse of a typed or picked time string into 24-hour "HH:MM", or
// null if it can't be understood. Accepts "2:30 PM", "2:30pm", "14:30", "2pm".
function parseTimeInput(text) {
  if (!text) return null;
  const m = text.trim().match(/^(\d{1,2}):?(\d{2})?\s*(am|pm)?$/i);
  if (!m) return null;
  let hour = parseInt(m[1], 10);
  const minute = m[2] ? parseInt(m[2], 10) : 0;
  const period = m[3] ? m[3].toLowerCase() : null;
  if (minute > 59) return null;
  if (period) {
    if (hour < 1 || hour > 12) return null;
    hour = period === 'am' ? (hour === 12 ? 0 : hour) : (hour === 12 ? 12 : hour + 12);
  } else if (hour > 23) return null;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function StatBox({ label, value, color, sub }) {
  return (
    <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '14px 18px', minWidth: 100 }}>
      <div style={{ fontSize: 28, fontWeight: 800, fontFamily: 'Syne, sans-serif', color: color || 'var(--text-primary)', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 5 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ─── Schedule Interview Modal ───────────────────────────────────────────────
function ScheduleInterviewModal({ pitch, googleAccounts, onClose, onScheduled }) {
  const toast = useToast();
  const [interviewType, setInterviewType] = useState('Video');
  const [date, setDate] = useState('');
  const [startTimeText, setStartTimeText] = useState('');
  const [endTimeText, setEndTimeText] = useState('');
  const [timeZone, setTimeZone] = useState(LOCAL_TZ);
  const [accountEmail, setAccountEmail] = useState(pitch.senderEmail || googleAccounts[0]?.email || '');
  const [interviewLink, setInterviewLink] = useState(pitch.interviewLink || '');
  const [contactInfo, setContactInfo] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!accountEmail) {
      toast('Select which Google account should create the invite', 'error');
      return;
    }
    const startTime = parseTimeInput(startTimeText);
    const endTime = parseTimeInput(endTimeText);
    if (!date || !startTime || !endTime) {
      toast('Enter a date and valid start/end times (e.g. "2:30 PM")', 'error');
      return;
    }

    setSaving(true);
    try {
      const res = await pitchesApi.scheduleInterview(pitch.id, {
        interviewType,
        startDateTime: `${date}T${startTime}:00`,
        endDateTime: `${date}T${endTime}:00`,
        timeZone,
        accountEmail,
        interviewLink: interviewLink || null,
        contactInfo: contactInfo || null,
      });
      toast('Interview scheduled — event created, no attendees added ✓', 'success');
      onScheduled(res.data.pitch);
    } catch (e) {
      toast(e.response?.data?.error || 'Could not create the calendar event', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <div><h2>Schedule — {pitch.podcastName}</h2></div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -8, marginBottom: 16 }}>
          Creates a Calendar event with no attendees — add them yourself in Google Calendar — and marks this podcast Booked.
        </p>
        <div className="form-group">
          <label>Create invite on</label>
          <select value={accountEmail} onChange={e => setAccountEmail(e.target.value)}>
            {googleAccounts.length === 0 && <option value="">(no connected accounts)</option>}
            {googleAccounts.map(a => (
              <option key={a.email} value={a.email}>{a.email}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Interview Type</label>
          <select value={interviewType} onChange={e => setInterviewType(e.target.value)}>
            <option value="Video">Video</option>
            <option value="Audio">Audio</option>
            <option value="In Person">In Person</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label>Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} autoFocus />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label>Start Time</label>
            <input
              type="text"
              list="schedule-time-slots"
              value={startTimeText}
              onChange={e => setStartTimeText(e.target.value)}
              placeholder="e.g. 2:30 PM"
              autoComplete="off"
            />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label>End Time</label>
            <input
              type="text"
              list="schedule-time-slots"
              value={endTimeText}
              onChange={e => setEndTimeText(e.target.value)}
              placeholder="e.g. 3:00 PM"
              autoComplete="off"
            />
          </div>
        </div>
        <datalist id="schedule-time-slots">
          {TIME_SLOTS.map(slot => <option key={slot} value={slot} />)}
        </datalist>
        <div className="form-group">
          <label>Time Zone</label>
          <select value={timeZone} onChange={e => setTimeZone(e.target.value)}>
            {TIMEZONE_OPTIONS.map(tz => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Interview Link</label>
          <input value={interviewLink} onChange={e => setInterviewLink(e.target.value)} placeholder="Zoom / Riverside / etc." />
        </div>
        <div className="form-group">
          <label>Podcast Contact Info (optional)</label>
          <textarea value={contactInfo} onChange={e => setContactInfo(e.target.value)} rows={2} placeholder="Socials, phone, website, etc." />
        </div>
        <div className="flex-center gap-2" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Scheduling...' : 'Create Invite & Mark Booked'}</button>
        </div>
      </div>
    </div>
  );
}

export default function ClientProfile() {
  const { id } = useParams();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('saved');
  const [googleAccounts, setGoogleAccounts] = useState([]);
  const [scheduleModal, setScheduleModal] = useState(null);

  const load = () => {
    setLoading(true);
    clientsApi.profile(id).then(r => setData(r.data)).catch(() => toast('Could not load profile', 'error')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    googleApi.getStatus().then(r => setGoogleAccounts(r.data.accounts || []));
  }, []);

  const handleAccountChange = async (e) => {
    const accountEmail = e.target.value || null;
    try {
      await clientsApi.update(id, { googleAccountEmail: accountEmail });
      setData(prev => ({ ...prev, client: { ...prev.client, googleAccountEmail: accountEmail } }));
      toast(accountEmail ? `Emails will send from ${accountEmail}` : 'Account unassigned', 'success');
    } catch { toast('Could not update account', 'error'); }
  };

  const handleFlag = async (pitch, weekKey) => {
    try {
      if (!weekKey) {
        await pitchesApi.unflag(pitch.id);
        toast('Unflagged', 'info');
      } else {
        await pitchesApi.flag(pitch.id, weekKey);
        toast('Flagged for ' + weekKey + ' ✓', 'success');
      }
      load();
    } catch (e) { toast('Error', 'error'); }
  };

  // Generate next 8 weeks as options
  const getWeekOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 8; i++) {
      const d = new Date(now);
      d.setDate(now.getDate() + i * 7);
      const startOfYear = new Date(d.getFullYear(), 0, 1);
      const week = Math.ceil(((d - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7);
      const key = d.getFullYear() + '-W' + week;
      const label = i === 0 ? 'This week' : i === 1 ? 'Next week' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      options.push({ key, label });
    }
    return options;
  };

  const handleArchiveOffer = async (pitch) => {
    try {
      await pitchesApi.update(pitch.id, { offeredArchived: true });
      toast('Hidden from offered list', 'info');
      load();
    } catch (e) { toast('Error', 'error'); }
  };

  const handleReImport = async () => {
    if (!window.confirm('Re-run the booking report import? This will re-match all podcasts.')) return;
    try {
      const res = await pitchingApi.reimportBooking(id);
      toast(`Matched ${res.data.matched} podcasts. ${res.data.unmatched?.length || 0} unmatched.`, 'success');
      load();
    } catch (e) { toast('Import failed: ' + (e.response?.data?.error || e.message), 'error'); }
  };

  if (loading) return <div className="page-content"><div className="empty-state"><p>Loading...</p></div></div>;
  if (!data) return <div className="page-content"><div className="empty-state"><p>Client not found</p></div></div>;

  const { client, savedPodcasts, offered, stats, weekKey } = data;
  const covered = savedPodcasts.length > 0;
  const flaggedThisWeek = savedPodcasts.filter(p => p.flaggedWeek === weekKey);

  // Offered this month
  const now = new Date();
  const offeredThisMonth = offered.filter(p => {
    if (!p.opportunityDraftedAt) return false;
    const d = new Date(p.opportunityDraftedAt);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });

  // Offered but not yet booked (pending — excluded from history)
  const offeredNotBooked = offered.filter(p => p.status === 'offered' && !p.offeredArchived);

  // Booking history — completed outcomes only (not pending offered ones)
  const bookingHistory = offered.filter(p => ['booked', 'declined', 'cancelled'].includes(p.status));

  return (
    <div className="page-content">
      {/* Back */}
      <Link to="/clients" style={{ fontSize: 12, color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 16 }}>← Clients</Link>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
        <div style={{
          width: 52, height: 52, borderRadius: 14, flexShrink: 0,
          background: covered ? 'var(--green-soft)' : 'var(--amber-soft)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 22,
          color: covered ? 'var(--green)' : 'var(--amber)'
        }}>
          {client.name.charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 24, marginBottom: 2 }}>{client.name}</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{client.email}</div>
        </div>
        <span className={`badge ${covered ? 'badge-green' : 'badge-amber'}`} style={{ fontSize: 13, padding: '5px 12px' }}>
          {covered ? '✓ Covered This Week' : '⚠ Needs Podcast This Week'}
        </span>
        {client.bookingReportSheetId && (
          <a href={`https://docs.google.com/spreadsheets/d/${client.bookingReportSheetId}/edit`}
            target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">↗ Booking Report</a>
        )}
        <button className="btn btn-ghost btn-sm" onClick={handleReImport} title="Re-run booking report import">↺ Re-import</button>
        {googleAccounts.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Send from:</span>
            <select
              value={client.googleAccountEmail || ''}
              onChange={handleAccountChange}
              style={{
                fontSize: 12, padding: '4px 8px', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)', background: 'var(--bg-elevated)',
                color: 'var(--text-primary)', cursor: 'pointer', maxWidth: 200
              }}
            >
              <option value="">— not set —</option>
              {googleAccounts.map(a => (
                <option key={a.email} value={a.email}>{a.email}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Key stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 28, flexWrap: 'wrap' }}>
        <StatBox label="Saved Pool" value={savedPodcasts.length} color="var(--green)" sub="ready to offer" />
        <StatBox label="Flagged This Week" value={flaggedThisWeek.length} color={flaggedThisWeek.length > 0 ? 'var(--accent)' : undefined} />
        <StatBox label="Offered This Month" value={offeredThisMonth.length} color="var(--blue)" />
        <StatBox label="Offered, Not Booked" value={offeredNotBooked.length} color={offeredNotBooked.length > 0 ? 'var(--amber)' : undefined} />
        <StatBox label="Total Booked" value={stats.booked} color="var(--green)" />
      </div>

      {/* Tabs */}
      <div className="tabs">
        <div className={`tab ${tab === 'saved' ? 'active' : ''}`} onClick={() => setTab('saved')}>
          Saved Pool ({savedPodcasts.length})
        </div>
        <div className={`tab ${tab === 'offered' ? 'active' : ''}`} onClick={() => setTab('offered')}>
          Offered — Not Booked ({offeredNotBooked.length})
        </div>
        <div className={`tab ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')}>
          Booking History ({bookingHistory.length})
        </div>
      </div>

      {/* Saved Pool */}
      {tab === 'saved' && (
        <div>
          {savedPodcasts.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🎙</div>
              <h3>No saved podcasts</h3>
              <p>When a podcast says yes, click "Said Yes" in the Podcasts tab.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {savedPodcasts
                .sort((a, b) => {
                  // This week first, then by flagged week, then unflagged last
                  const aIsThisWeek = a.flaggedWeek === weekKey;
                  const bIsThisWeek = b.flaggedWeek === weekKey;
                  if (aIsThisWeek && !bIsThisWeek) return -1;
                  if (!aIsThisWeek && bIsThisWeek) return 1;
                  if (a.flaggedWeek && !b.flaggedWeek) return -1;
                  if (!a.flaggedWeek && b.flaggedWeek) return 1;
                  return 0;
                })
                .map(pitch => {
                  const isFlagged = pitch.flaggedWeek === weekKey;
                  return (
                    <div key={pitch.id} className="card" style={{
                      display: 'flex', alignItems: 'center', gap: 14,
                      borderLeft: `3px solid ${isFlagged ? 'var(--green)' : pitch.flaggedWeek ? 'var(--accent)' : 'var(--border)'}`
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{pitch.podcastName}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                          {pitch.hostName && <span>{pitch.hostName}</span>}
                          {pitch.monthlyListeners && <span style={{ marginLeft: 8 }}>· {pitch.monthlyListeners} listeners</span>}
                        </div>
                        {pitch.opportunityNotes && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{pitch.opportunityNotes}</div>}
                        {pitch.flaggedWeek && pitch.flaggedWeek !== weekKey && (
                          <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 4 }}>📌 Flagged for {pitch.flaggedWeek}</div>
                        )}
                      </div>
                      {pitch.podcastLink && <a href={pitch.podcastLink} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--accent)' }}>↗</a>}
                      {isFlagged && <span className="badge badge-green">This Week</span>}
                      <div className="flex-center gap-2">
                        <select
                          value={pitch.flaggedWeek || ''}
                          onChange={e => handleFlag(pitch, e.target.value || null)}
                          onClick={e => e.stopPropagation()}
                          style={{ padding: '5px 10px', fontSize: 12, width: 'auto', minWidth: 130 }}
                        >
                          <option value="">Not scheduled</option>
                          {getWeekOptions().map(o => (
                            <option key={o.key} value={o.key}>{o.label} ({o.key})</option>
                          ))}
                        </select>

                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}

      {/* Offered not booked */}
      {tab === 'offered' && (
        <div>
          {offeredNotBooked.length === 0 ? (
            <div className="empty-state"><div className="empty-state-icon">✓</div><h3>All offered podcasts are booked</h3></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[...offeredNotBooked].sort((a, b) => {
                const aKey = a.opportunityDraftedAt ? new Date(a.opportunityDraftedAt).getTime() : (a.bookingReportRowNum || 0);
                const bKey = b.opportunityDraftedAt ? new Date(b.opportunityDraftedAt).getTime() : (b.bookingReportRowNum || 0);
                return bKey - aKey;
              }).map(pitch => (
                <div key={pitch.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{pitch.podcastName}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{pitch.hostName}{pitch.monthlyListeners && ` · ${pitch.monthlyListeners}`}</div>
                  </div>
                  {pitch.podcastLink && <a href={pitch.podcastLink} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--accent)' }}>↗</a>}
                  <span className="badge badge-amber">Offered</span>
                  <button className="btn btn-sm btn-primary"
                    style={{ fontSize: 11 }}
                    onClick={() => setScheduleModal(pitch)}>
                    Schedule
                  </button>
                  <button className="btn btn-sm btn-secondary"
                    style={{ color: 'var(--text-muted)', fontSize: 11 }}
                    onClick={() => handleArchiveOffer(pitch)}>
                    Archive
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Booking history */}
      {tab === 'history' && (
        <div>
          {bookingHistory.length === 0 ? (
            <div className="empty-state"><div className="empty-state-icon">📋</div><h3>Nothing booked or declined yet</h3></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[...bookingHistory].sort((a, b) => {
                const aKey = a.opportunityDraftedAt ? new Date(a.opportunityDraftedAt).getTime() : (a.bookingReportRowNum || 0);
                const bKey = b.opportunityDraftedAt ? new Date(b.opportunityDraftedAt).getTime() : (b.bookingReportRowNum || 0);
                return bKey - aKey;
              }).map(pitch => {
                const isRealDate = pitch.interviewDate &&
                  !['pending', 'declined', 'cancelled'].includes(pitch.interviewDate.toLowerCase());
                return (
                  <div key={pitch.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{pitch.podcastName}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{pitch.hostName}{pitch.monthlyListeners && ` · ${pitch.monthlyListeners}`}</div>
                    </div>
                    {pitch.podcastLink && <a href={pitch.podcastLink} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--accent)' }}>↗</a>}
                    <span className={`badge ${pitch.status === 'booked' ? 'badge-green' : pitch.status === 'declined' ? 'badge-red' : 'badge-muted'}`}>
                      {pitch.status === 'booked' ? '🎙 Booked' : pitch.status === 'declined' ? 'Declined' : 'Cancelled'}
                    </span>
                    {isRealDate && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{pitch.interviewDate}</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {scheduleModal && (
        <ScheduleInterviewModal
          pitch={scheduleModal}
          googleAccounts={googleAccounts}
          onClose={() => setScheduleModal(null)}
          onScheduled={() => { setScheduleModal(null); load(); }}
        />
      )}
    </div>
  );
}
